import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popup } from '@progress/kendo-react-popup';
import { Button } from 'components/button/Button';
import {
    FullScreenContainer,
    FullscreenContent,
    FullscreenHeader,
    PopupContainer,
    PopupContent,
    PopupOverlay,
} from 'components/popup/stateless_popup/styled';
import { StatelessPopupProps } from 'components/popup/stateless_popup/types';
import { ConfiguredAppProvider } from 'contexts/ConfiguredAppProvider';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import { forwardRef, Ref } from 'react';
import { kendoPopupZIndex, modalOverlayZIndex } from 'styles/variables';
import { updateRef } from 'utils/mergeRefs';

export const StatelessPopup = forwardRef(
    (
        {
            anchor,
            anchorAlign,
            appendTo,
            popupAlign,
            collision,
            children,
            isFullscreen,
            fullscreenHeader,
            fullscreenZIndex = modalOverlayZIndex,
            popupZIndex = kendoPopupZIndex,
            popupClassName,
            popupSettings,
            noPadding = false,
            noBoxShadow = false,
            noPopupBackground = false,
            ariaLabel = 'popup',
            onClose,
            noOverlay = false,
        }: StatelessPopupProps,
        ref: Ref<HTMLDivElement>
    ) => {
        const defaultIsFullscreen = useIsViewportAtMost(MediaSize.xsmall);
        const renderFullscreen = isFullscreen ?? defaultIsFullscreen;

        return renderFullscreen ? (
            <FullScreenContainer
                className="flex-column"
                $fullscreenZIndex={fullscreenZIndex}
                aria-label={ariaLabel}
                ref={ref}
            >
                <FullscreenHeader className="flex-row flex-nowrap align-center">
                    <div className="flex-grow-1">{fullscreenHeader}</div>
                    <Button
                        type="button"
                        className="flex-row align-center"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        Close
                        <FontAwesomeIcon className="pl-10 pt-2" icon={faTimes} />
                    </Button>
                </FullscreenHeader>
                <FullscreenContent
                    className="flex-grow-1 scrollable-y"
                    $noPadding={noPadding}
                >
                    {children}
                </FullscreenContent>
            </FullScreenContainer>
        ) : (
            <Popup
                {...popupSettings}
                appendTo={appendTo}
                animate={false}
                anchor={anchor}
                anchorAlign={anchorAlign}
                popupAlign={popupAlign}
                collision={collision}
                className={`stateless-popup-container popup-scroll-container kcf-popup ${
                    noBoxShadow ? '' : 'popup-box-shadow'
                } ${popupClassName ?? ''}`}
                show
                style={{
                    zIndex: popupZIndex,
                    borderRadius: popupSettings?.borderRadius ?? '0.5rem',
                    ...(popupSettings?.style ?? {}),
                }}
            >
                <ConfiguredAppProvider>
                    <PopupContainer
                        className="position-relative"
                        $popupZIndex={popupZIndex}
                        aria-label={ariaLabel}
                        // This applies the correct element to the ref. Kendo's popup renders the scrollbar, which
                        // is outside of our content. We want to prevent the scroll bar as being interpreted
                        // as "outside" and triggering the 'onClickOutside` to close the modal
                        ref={(elem) =>
                            updateRef(ref, elem?.closest('.popup-scroll-container'))
                        }
                    >
                        {!noOverlay && (
                            <PopupOverlay
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onClose();
                                }}
                            />
                        )}
                        <PopupContent
                            className="position-relative"
                            $noPadding={noPadding}
                            $noPopupBackground={noPopupBackground}
                            style={{
                                borderRadius: popupSettings?.borderRadius ?? '0.5rem',
                                overflow: popupSettings?.style?.overflow ?? 'hidden',
                            }}
                        >
                            {children}
                        </PopupContent>
                    </PopupContainer>
                </ConfiguredAppProvider>
            </Popup>
        );
    }
);
